<template>
  <div>
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div class="detail-container">
        <!-- Header -->
        <div class="detail-header" :class="'header-' + (order.orderStatus ? order.orderStatus.toLowerCase() : '')">
          <div class="detail-header-top">
            <div>
              <span v-if="hasMultiLocations()" class="detail-location">{{ getLocationName(order.locationId) }}</span>
              <div class="detail-order-type">{{ getOrderTypeText() }}</div>
              <div class="detail-order-id">Ordre #{{ order.orderId }}</div>
              <div class="detail-status-row mt-2">
                <span class="detail-status-badge" :class="'status-' + (order.orderStatus ? order.orderStatus.toLowerCase() : '')">{{ getOrderStatusText() }}</span>
                <button v-if="isPaid && !isRefunded && !showReceiptEmailInput" type="button" class="bo_btn btn-send-receipt" @click="showReceiptEmailInput = true">
                  <font-awesome-icon :icon="['fas', 'paper-plane']" class="me-1" />
                  Send kvittering
                </button>
                <button v-if="isSuperAdmin && !showReceiptEmailInput" type="button" class="bo_btn btn-repair-order" @click="openRepairEditor()">
                  <font-awesome-icon :icon="['fas', 'wrench']" class="me-1" />
                  Repair order data
                </button>
              </div>
              <div v-if="showReceiptEmailInput" class="receipt-email-row mt-2">
                <input v-model="receiptEmail" type="email" class="receipt-email-input" placeholder="E-postadresse" @keyup.enter="sendReceipt()" />
                <button type="button" class="bo_btn btn-send-receipt" @click="sendReceipt()" :disabled="sendingReceipt || !receiptEmail">
                  <font-awesome-icon :icon="['fas', 'paper-plane']" class="me-1" />
                  {{ sendingReceipt ? 'Sender...' : 'Send' }}
                </button>
                <button type="button" class="btn-cancel-receipt" @click="showReceiptEmailInput = false; receiptEmail = ''">
                  <font-awesome-icon :icon="['fas', 'times']" />
                </button>
              </div>
            </div>
          </div>
          <button v-if="isSuperAdmin && !isOrderCompletedOrCancelled" type="button" class="btn-mark-completed" @click="markAsCompleted()">
            <font-awesome-icon :icon="['fas', 'check']" class="me-1" />
            Merk som fullført
          </button>
        </div>

        <!-- A delregning: one guest's part of a table's bill, a sale of its own. Its table's order is one click away. -->
        <div v-if="order.splitFrom" class="detail-section detail-split-section">
          <div class="detail-section-title">
            <font-awesome-icon :icon="['fas', 'cut']" class="me-2" />
            Delregning
          </div>
          <div class="detail-split-text">
            Del {{ order.splitFrom.part }} av regningen for ordre #{{ order.splitFrom.orderId }}<span v-if="order.tableName">, bord {{ order.tableName }}</span>.
            Gjesten betalte sin del som et eget salg, med egen kvittering.
          </div>
          <button type="button" class="bo_btn_secondary mt-2" @click="openOrder(order.splitFrom.orderDocumentId)">
            <font-awesome-icon :icon="['fas', 'external-link-alt']" class="me-1" />
            Åpne ordre #{{ order.splitFrom.orderId }}
          </button>
        </div>

        <!-- Customer Section -->
        <div class="detail-section" v-if="order.name || order.mobileNumber || order.tableName">
          <div class="detail-section-title">
            <font-awesome-icon :icon="['fas', 'user']" class="me-2" />
            Kundeinformasjon
          </div>
          <div class="detail-info-grid">
            <div v-if="order.name" class="detail-info-row">
              <span class="detail-label">Navn</span>
              <span class="detail-value detail-name">{{ getNameText(order.name) }}</span>
            </div>
            <div v-if="order.mobileNumber" class="detail-info-row">
              <span class="detail-label">Telefon</span>
              <span class="detail-value">{{ getPhoneNrText(order.mobileNumber) }}</span>
            </div>
            <div v-if="order.tableName" class="detail-info-row">
              <span class="detail-label">Bord</span>
              <span class="detail-value">{{ order.tableName }}</span>
            </div>
          </div>
        </div>

        <!-- Delivery address — only for DELIVERY, and only when the order actually carries one. Kiosk
             and counter orders have no address, and a heading over three empty lines helps nobody. -->
        <div v-if="deliveryAddress" class="detail-section">
          <div class="detail-section-title">
            <font-awesome-icon :icon="['fas', 'map-marker-alt']" class="me-2" />
            Leveringsadresse
          </div>
          <div class="detail-info-grid">
            <div class="detail-info-row">
              <span class="detail-label">Adresse</span>
              <span class="detail-value detail-address">
                <!-- The address IS the link: on a phone this opens the map app, which is the whole
                     reason anyone reads it here. -->
                <a :href="deliveryAddress.mapUrl" target="_blank" rel="noopener">
                  <span v-for="(line, index) in deliveryAddress.lines" :key="index" class="d-block">{{ line }}</span>
                </a>
              </span>
            </div>
            <div v-if="order.floor" class="detail-info-row">
              <span class="detail-label">Etasje</span>
              <span class="detail-value">{{ order.floor }}</span>
            </div>
            <div v-if="order.nameOnDoorbell" class="detail-info-row">
              <span class="detail-label">Navn på ringeklokke</span>
              <span class="detail-value">{{ order.nameOnDoorbell }}</span>
            </div>
          </div>
        </div>

        <!-- Order Info Section -->
        <div class="detail-section">
          <div class="detail-section-title">
            <font-awesome-icon :icon="['fas', 'receipt']" class="me-2" />
            Ordreinformasjon
          </div>
          <div class="detail-info-grid">
            <div class="detail-info-row">
              <span class="detail-label">Bestilt via</span>
              <span class="detail-value">{{ getChannelText() }}</span>
            </div>
            <div class="detail-info-row">
              <span class="detail-label">Bestilt</span>
              <span class="detail-value">{{ getDateTimeText(order.orderedTime) }}</span>
            </div>
            <div v-if="order.presentCounter && useOrderDisplay()" class="detail-info-row">
              <span class="detail-label">Bestillingsnr</span>
              <span class="detail-value detail-present-counter">{{ order.presentCounter }}</span>
            </div>
            <div v-if="order.orderType == 'PICKUP'" class="detail-info-row">
              <span class="detail-label">Hentes</span>
              <span class="detail-value">{{ getPickupOrDeliveryTimeText(order) }}</span>
            </div>
            <div v-else-if="order.orderType == 'DELIVERY'" class="detail-info-row">
              <span class="detail-label">Leveres</span>
              <span class="detail-value">{{ getPickupOrDeliveryTimeText(order) }}</span>
            </div>
          </div>
        </div>

        <!-- Payment Section -->
        <div class="detail-section">
          <div class="detail-section-title">
            <font-awesome-icon :icon="['fas', 'credit-card']" class="me-2" />
            Betaling
          </div>
          <div class="detail-info-grid">
            <div class="detail-info-row">
              <span class="detail-label">Betalt med</span>
              <span class="detail-value">
                <span class="detail-payment-badge" :class="'payment-' + paymentBadgeKind + '-badge'">
                  <font-awesome-icon :icon="paymentDisplay.icon" class="me-1" />{{ paymentDisplay.text }}
                </span>
              </span>
            </div>
            <div class="detail-info-row">
              <span class="detail-label">Totalbeløp</span>
              <span class="detail-value detail-amount">{{ displayTwoDecimals(order.totalAmount) }}</span>
            </div>
            <div v-if="order.tipsAmount > 0" class="detail-info-row">
              <span class="detail-label">Tips</span>
              <span class="detail-value">{{ displayTwoDecimals(order.tipsAmount) }}</span>
            </div>
            <div v-if="order.manualDiscount > 0" class="detail-info-row">
              <span class="detail-label">Rabatt</span>
              <span class="detail-value detail-discount">-{{ displayTwoDecimals(order.manualDiscount) }}</span>
            </div>
          </div>
          <div v-if="order.tipsAmount > 0 || order.manualDiscount > 0" class="detail-total-row">
            <span>Å betale</span>
            <span class="detail-total-amount">{{ getToPayAmount() }}</span>
          </div>
        </div>

        <!-- Every payment on the order: how, where, when, and the tip in it -->
        <div v-if="payments.length > 0" class="detail-section">
          <div class="detail-section-title">
            <font-awesome-icon :icon="['fas', 'list']" class="me-2" />
            Betalinger
          </div>
          <div class="detail-line-list">
            <div v-for="(payment, index) in payments" :key="payment.id || index" class="detail-line-item">
              <div class="detail-line-item-info">
                <div class="detail-line-item-name">
                  <font-awesome-icon :icon="paymentLine(payment).icon" class="me-1" />{{ paymentLine(payment).text }}
                </div>
                <div class="detail-line-item-sub">{{ paymentWhere(payment) }}</div>
                <div v-if="payment.transactionId" class="detail-line-item-sub">Transaksjon {{ payment.transactionId }}</div>
              </div>
              <div class="detail-line-item-amount">
                {{ displayTwoDecimals(payment.amount) }}
                <div v-if="payment.tipsAmount > 0" class="detail-line-item-sub">herav tips {{ displayTwoDecimals(payment.tipsAmount) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- The table's delregninger: each guest's part, a sale of its own, one click away -->
        <div v-if="splitParts.length > 0" class="detail-section detail-split-section">
          <div class="detail-section-title">
            <font-awesome-icon :icon="['fas', 'cut']" class="me-2" />
            Delregninger
          </div>
          <div class="detail-split-text">
            Gjestene betalte hver sin del av regningen. Hver del er et eget salg med egen kvittering, og denne ordren
            har det som ikke var betalt da.
          </div>
          <div class="detail-line-list">
            <div v-for="part in splitParts" :key="part.orderDocumentId" class="detail-line-item">
              <div class="detail-line-item-info">
                <div class="detail-line-item-name">Del {{ part.part }} · ordre #{{ part.orderId }}</div>
                <div class="detail-line-item-sub">{{ getPaymentMethodText(part.paymentMethod) }}<span v-if="part.paidTime"> · {{ getDateTimeTextShort(part.paidTime) }}</span></div>
              </div>
              <div class="detail-line-item-actions">
                <span class="detail-line-item-amount">{{ displayTwoDecimals(part.toPayAmount) }}</span>
                <button type="button" class="bo_btn_secondary detail-split-open" @click="openOrder(part.orderDocumentId)">Åpne</button>
              </div>
            </div>
            <div class="detail-line-item">
              <div class="detail-line-item-info">
                <div class="detail-line-item-name">{{ isPaid ? 'Siste del, betalt på denne ordren' : 'Igjen å betale på denne ordren' }}</div>
              </div>
              <div class="detail-line-item-amount">{{ getToPayAmount() }}</div>
            </div>
          </div>
          <div class="detail-total-row">
            <span>Hele regningen</span>
            <span class="detail-total-amount">{{ displayTwoDecimals(splitTotal) }}</span>
          </div>
        </div>

        <!-- Refunded Items Section -->
        <div v-if="showRefundCartItems()" class="detail-section detail-refund-section">
          <div class="detail-section-title detail-refund-title">
            <font-awesome-icon :icon="['fas', 'undo-alt']" class="me-2" />
            Refundert
          </div>
          <div class="detail-refund-list">
            <div v-for="refundedCartItem in refundedCartItems" :key="refundedCartItem.id" :id="refundedCartItem.id" class="detail-refund-item">
              <div class="detail-refund-item-info">
                <div class="detail-refund-item-name">-1 x {{ refundedCartItem.itemName }}{{ shareSuffix(refundedCartItem) }}</div>
                <div v-if="refundedCartItem.selectedItemSubselection !== ''" class="detail-refund-item-sub">{{ refundedCartItem.selectedItemSubselection }}</div>
                <div v-if="refundedCartItem.spicyLevel !== ''" class="detail-refund-item-sub">{{ refundedCartItem.spicyLevel }}</div>
                <div v-if="refundedCartItem.comment !== ''" class="detail-refund-item-sub">{{ refundedCartItem.comment }}</div>
              </div>
              <div class="detail-refund-item-price">-{{ displayTwoDecimals(getPriceWithVat(refundedCartItem, manualDiscountPercent)) }}</div>
            </div>
            <div v-if="order.refundedTipsAmount > 0" class="detail-refund-item">
              <div class="detail-refund-item-info">
                <div class="detail-refund-item-name">-Tips</div>
              </div>
              <div class="detail-refund-item-price">-{{ displayTwoDecimals(order.refundedTipsAmount) }}</div>
            </div>
          </div>
          <div v-if="refundRounding !== 0" class="detail-refund-item">
            <div class="detail-refund-item-info">
              <div class="detail-refund-item-name">Øreavrunding</div>
            </div>
            <div class="detail-refund-item-price">{{ refundRounding < 0 ? '+' : '-' }}{{ displayTwoDecimals(Math.abs(refundRounding)) }}</div>
          </div>
          <div class="detail-refund-total">
            <span>Totalt refundert</span>
            <span class="detail-refund-total-amount">-{{ displayTwoDecimals(refundedTotalAmount) }}</span>
          </div>
        </div>

        <!-- Order Description -->
        <div v-if="order.description" class="detail-section">
          <div class="detail-section-title">
            <font-awesome-icon :icon="['fas', 'file-alt']" class="me-2" />
            Ordrebeskrivelse
          </div>
          <div class="detail-description">{{ order.description }}</div>
        </div>

        <!-- Refund Actions -->
        <div v-if="showRefundInfo">
            <div v-if="showRefundBtn()" class="detail-section">
              <button type="button" class="btn-refund" @click="goToOrderRefundPage()">
                <font-awesome-icon :icon="['fas', 'undo-alt']" class="me-1" />
                Refunder
              </button>
            </div>

            <!-- SUPER_ADMIN, for now: a till or kiosk order paid only by VIVA card — or a pay-my-part paid only by
                 VIVA card — can be refunded without the guest's card. Cash stays a refund at the till. -->
            <div v-if="vivaTerminalRefundRoute()" class="detail-section">
              <button type="button" class="btn-refund" @click="goToVivaTerminalRefundPage()">
                <font-awesome-icon :icon="['fas', 'undo-alt']" class="me-1" />
                Refunder Viva-kortbetaling
              </button>
              <div class="detail-refund-hint">Kun superadmin · kunden trenger ikke være til stede</div>
            </div>

            <div v-if="showInfoRefundNotPossible() || showInfoTerminalRefundNotPossible()" class="detail-section">
              <div class="detail-refund-notice">
                <font-awesome-icon :icon="['fas', 'info-circle']" class="me-2" />
                <span>Ordren har blitt tatt med i dagsoppgjøret og kan ikke refunderes.</span>
              </div>
            </div>
        </div>
      </div>

      <!-- Repair Editor (superadmin) -->
      <div v-if="isSuperAdmin && showRepairEditor" class="repair-overlay" @click.self="closeRepairEditor()">
        <div class="repair-modal">
          <div class="repair-modal-header">
            <font-awesome-icon :icon="['fas', 'wrench']" class="me-2" />
            Repair order data
            <button type="button" class="repair-close" @click="closeRepairEditor()">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>

          <div class="repair-modal-body">
            <p class="repair-help">Choose the order type. Cart items, VAT and totals are recalculated from the menu prices for that type — verify the new amount matches what was actually paid before applying.</p>

            <div class="repair-type-toggle">
              <button type="button" class="bo_btn repair-type-btn" :class="{ 'repair-type-active': repairOrderType === 'DINE_IN' }" @click="setRepairOrderType('DINE_IN')">Spise her (Dine-in)</button>
              <button type="button" class="bo_btn repair-type-btn" :class="{ 'repair-type-active': repairOrderType === 'PICKUP' }" @click="setRepairOrderType('PICKUP')">Ta med (Take-out)</button>
            </div>

            <div v-if="repairLoadingItems" class="repair-note">Laster varer...</div>

            <table v-else class="repair-table">
              <thead>
                <tr><th>Vare</th><th class="repair-num">Antall</th><th class="repair-num">Pris</th><th class="repair-num">Sum</th></tr>
              </thead>
              <tbody>
                <tr v-for="line in repairedLines" :key="line.id" :class="{ 'repair-line-missing': line.missing }">
                  <td>{{ line.name }}<span v-if="line.missing" class="repair-missing-tag"> (vare ikke funnet – uendret)</span></td>
                  <td class="repair-num">{{ line.count }}</td>
                  <td class="repair-num">{{ displayTwoDecimals(line.unitGross) }}</td>
                  <td class="repair-num">{{ displayTwoDecimals(line.lineGross) }}</td>
                </tr>
              </tbody>
            </table>

            <div class="repair-totals">
              <div class="repair-row"><span>Totalbeløp</span><span>{{ displayTwoDecimals(repairedTotalAmount) }}</span></div>
              <div class="repair-row"><span>Herav MVA ({{ repairOrderType === 'DINE_IN' ? vat.dineIn : vat.takeOut }}%)</span><span>{{ displayTwoDecimals(repairedTotalVat) }}</span></div>
              <div class="repair-row repair-row-total"><span>Å betale</span><span>{{ displayTwoDecimals(repairedToPayAmount) }}</span></div>
              <div class="repair-row repair-compare" :class="repairMatchesPayment ? 'repair-ok' : 'repair-warn'">
                <span>Faktisk betalt</span>
                <span>{{ displayTwoDecimals(capturedPaymentsSum) }} {{ repairMatchesPayment ? '✓ stemmer' : '✗ stemmer ikke' }}</span>
              </div>
            </div>
          </div>

          <div class="repair-modal-footer">
            <button type="button" class="bo_btn repair-cancel" @click="closeRepairEditor()">Avbryt</button>
            <button type="button" class="bo_btn repair-apply" @click="applyRepair()" :disabled="repairingOrder || repairLoadingItems || !repairMatchesPayment">
              {{ repairingOrder ? 'Lagrer...' : 'Apply repair' }}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialogue ref="receiptDialog" />
  </div>
</template>

<script>
import OrderService from "@/services/order/OrderService";
import ProductService from "@/services/product/ProductService";
import OrderUtil from "@/util/OrderUtil";
import VatUtil from "@/util/VatUtil";
import MerchantUtil from "@/util/MerchantUtil";
import UserUtil from "@/util/UserUtil";
import LocationUtil from "@/util/LocationUtil";
import MathUtil from "@/util/MathUtil";
import LoadingSpinner from "@/components/other/LoadingSpinner.vue";
import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";

export default {
  components: {LoadingSpinner, ConfirmDialogue},
  props: {
    dataForOrderDetail: Object
  },
  emits: ['close', 'open-order'],
  data() {
    return {
      loading: false,
      sendingReceipt: false,
      repairingOrder: false,
      showReceiptEmailInput: false,
      receiptEmail: '',
      showRefundInfo: !UserUtil.isEmployee(), //hide refund for employee, employees often use week password, allow for refund only in POS, more secure.
      // Repair editor
      showRepairEditor: false,
      repairLoadingItems: false,
      repairOrderType: null,
      itemsById: {},
      repairedLines: [],
      repairedTotalAmount: 0,
      repairedTotalVat: 0,
      repairedToPayAmount: 0,
    };
  },
  computed: {
    /**
     * The delivery address as lines to print, plus a map link — or null when there is nothing to show.
     *
     * Street and house number are separate fields, but the storefront puts the whole street line in
     * `street` and leaves `houseNumber` empty, while POS and app orders may fill both; joining them
     * handles either without printing a stray space. Zip and city likewise: a partial address is still
     * worth showing, so each line is built from what exists rather than demanded whole.
     */
    deliveryAddress() {
      if (this.order.orderType !== 'DELIVERY') {
        return null;
      }
      const streetLine = [this.order.street, this.order.houseNumber].filter(Boolean).join(' ').trim();
      const cityLine = [this.order.zipCode, this.order.city].filter(Boolean).join(' ').trim();
      const lines = [streetLine, cityLine].filter(Boolean);
      if (lines.length === 0) {
        return null;
      }
      return {
        lines,
        mapUrl: 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(lines.join(', '))
      };
    },
    /**
     * Icon for how it was paid, text for what with — the card brand included.
     *
     * <p>COMPUTED, not a method. The template reads it as a value (`paymentDisplay.text`), and as a method
     * that is a property lookup on a function object: text `undefined`, icon `undefined`, and the
     * `!== 'Ikke betalt'` test true — so the badge rendered as an empty green pill rather than as an
     * error. The orders LIST was unaffected, because it calls OrderUtil directly, which is why this
     * survived: the same order showed «Betalt – Visa» one click away.
     */
    paymentDisplay(){
      return OrderUtil.getPaymentDisplay(this.order.payments);
    },
    /** unpaid / completed / paid — the same rule as the orders list (OrderUtil.getPaymentBadgeKind). */
    paymentBadgeKind(){
      return OrderUtil.getPaymentBadgeKind(this.paymentDisplay.text, this.order.orderStatus);
    },
    isSuperAdmin(){
      return UserUtil.isSuperAdmin();
    },
    isOrderCompletedOrCancelled(){
      return (this.order.orderStatus === 'COMPLETED' || this.order.orderStatus === 'CANCELLED');
    },
    orderDocumentId(){
      return this.dataForOrderDetail.order.id;
    },
    manualDiscountPercent(){
      if(this.dataForOrderDetail.order.manualDiscountPercent){
         return this.dataForOrderDetail.order.manualDiscountPercent;
      } else {
         return 0;
      }
    },
    order(){
      return this.dataForOrderDetail.order;
    },
    vat(){
      return this.dataForOrderDetail.vat;
    },
    refundedCartItems(){
      return this.getRefundedCartItems();
    },
    payments(){
      return this.order.payments || [];
    },
    /** On the table's order: the delregninger paid off it, in the order they were paid. */
    splitParts(){
      return this.order.splitInto || [];
    },
    /** The whole bill: every delregning, and what is on this order. */
    splitTotal(){
      let total = this.splitParts.reduce((sum, part) => sum + (part.toPayAmount || 0), 0);
      let manualDiscount = this.order.manualDiscount ? this.order.manualDiscount : 0;
      return total + this.order.totalAmount + (this.order.tipsAmount || 0) - manualDiscount;
    },
    refundedTotalAmount(){
      return this.order.refundedTotalAmount;
    },
    /** A cash refund's øreavrunding: the cash handed back (whole kroner) less what the refunded lines came to. */
    refundRounding(){
      return (this.order.refundedPayments || []).reduce((sum, payment) => sum + (payment.roundingAmount || 0), 0);
    },
    isRefunded(){
      if(!this.order){
        return false;
      }
      return (this.order.refundedTotalAmount > 0) ||
          (this.order.refundedCartItems !== undefined && this.order.refundedCartItems.length > 0);
    },
    /**
     * Whether the order is paid — via a payment provider/cash entry OR entirely with bonus points.
     * Mirrors the "paid" check used by the refund button so "Send kvittering" is only offered for
     * orders that actually have a receipt to send (an unpaid order has nothing to receipt).
     */
    isPaid(){
      if(!this.order){
        return false;
      }
      return OrderUtil.getPaymentStatus(this.order.payments) === 'PAID' || this.isPaidEntirelyWithBonus();
    },
    capturedPaymentsSum(){
      return this.getPaymentsSum();
    },
    repairMatchesPayment(){
      return Math.abs(this.repairedToPayAmount - this.capturedPaymentsSum) < 0.9;
    }
  },
  methods:{
    /** Opens another order in this panel: a delregning's table, or one of the table's delregninger. */
    openOrder(orderDocumentId){
      this.$emit('open-order', orderDocumentId);
    },
    paymentLine(payment){
      return OrderUtil.getPaymentLineDisplay(payment);
    },
    /**
     * Where and when it was paid: «Kasse 1 · 26.10.2026 21:04», or «Nett» for a payment made on the web. A cash
     * payment from the till's payment screen had no time of its own until 2026-10-28; when it is the order's only
     * payment, the order's is the same moment.
     */
    paymentWhere(payment){
      let where = payment.paidAtTerminalId ? ('Kasse ' + payment.paidAtTerminalId) : 'Nett';
      let paidTime = payment.paidTime || (this.payments.length === 1 ? this.order.paidTime : null);
      let when = paidTime ? (' · ' + OrderUtil.getDateTimeTextShort(paidTime)) : '';
      return where + when;
    },
    getPaymentMethodText(paymentMethod){
      return OrderUtil.getPaymentMethodText(paymentMethod);
    },
    getDateTimeTextShort(dateTime){
      return OrderUtil.getDateTimeTextShort(dateTime);
    },
    shareSuffix(cartItem){
      return OrderUtil.shareSuffix(cartItem);
    },
    hasMultiLocations(){
      return LocationUtil.hasMultiLocations();
    },
    getLocationName(locationId){
      let location = LocationUtil.getLocation(locationId);
      return location.name;
    },
    useOrderDisplay(){
      let location = LocationUtil.getLocation(this.order.locationId);
      return location !== undefined && location.useOrderDisplay === true;
    },
    getToPayAmount(){
      let manualDiscount = this.order.manualDiscount ? this.order.manualDiscount : 0;
      let toPayAmount = this.order.totalAmount + this.order.tipsAmount - manualDiscount;
      return this.displayTwoDecimals(toPayAmount);
    },
    getRefundedCartItems(){
      if(this.order.refundedCartItems !== undefined){
        let refundedCartItems = this.order.refundedCartItems;
        return refundedCartItems;
      } else {
        return [];
      }
    },
    getPriceWithVat(refundedCartItem, manualDiscountPercent){
      //refundedCartItem.price is the price with VAT after item discount has been subtracted.
      let priceWithManualDiscount = refundedCartItem.price * (100 - manualDiscountPercent) / 100;
      return MathUtil.roundTwoDecimals(priceWithManualDiscount);
    },
    getOrderTypeText(){
      return OrderUtil.getOrderTypeText(this.order.orderType).toUpperCase();
    },
    getNameText(name){
      return OrderUtil.getNameText(name);
    },
    getPhoneNrText(mobileNumber){
      return OrderUtil.getPhoneNrText(mobileNumber);
    },
    getOrderStatusText(){
       return OrderUtil.getOrderStatusText(this.order.orderStatus).toUpperCase();
    },
    getChannelText(){
      return OrderUtil.getChannelText(this.order);
    },
    getDateTimeText(dateTime){
       return OrderUtil.getDateTimeText(dateTime);
    },
    getPickupOrDeliveryTimeText(order){
      let pickupOrDeliveryTime = order.pickupOrDeliveryTime;
      if (pickupOrDeliveryTime) {
          return OrderUtil.getDateTimeTextShort(pickupOrDeliveryTime);
      } else {
          if(order.orderType == 'PICKUP') {
            let pickupTimeOption = order.pickupTimeOption;
            if(pickupTimeOption == 'whenFoodIsReady') {
              return "Får melding når maten er klar";
            }
          } else {
            //Delivery
            let deliveryTimeOption = order.deliveryTimeOption;
            if(deliveryTimeOption == 'asSoonAsPossible') {
                return "Så fort som mulig";
            }
          }
      }
    },

    /**
     * Only allow refund for orders that are
     * 1. TAKEAWAY/ALL Merchant
     * 2. Channel := WEBSHOP
     * 3. not settled yet AND
     * 4. paid (via payment provider OR entirely with bonus points) AND
     * 5. refundedCartItems length is 0 dvs. allow refund only once.
     *
     * Bonus refund: Orders paid fully or partially with bonus points (usedPoints > 0) are refundable.
     * Payment provider is refunded first, bonus points are returned only for the amount exceeding what
     * was charged to the payment provider. See getBonusRefundSplit() in OrderRefund.vue.
     */
    showRefundBtn(){
      if( (this.useFunctionality() === 'TAKEAWAY' || this.useFunctionality() === 'KIOSK_TAKEAWAY' || this.useFunctionality() === 'ALL') &&
          (this.order.channel === 'WEBSHOP') &&
          // !== true, not === undefined. "Not settled yet" is what the rule above means, and an order
          // carrying settled:false is exactly that — but it failed this test, and its counterpart
          // showInfoRefundNotPossible requires settled === true, so such an order got neither the button
          // nor the explanation for why it was missing. The two conditions are now complements and every
          // order falls into one of them.
          this.order.settled !== true &&
          (OrderUtil.getPaymentStatus(this.order.payments) === 'PAID' || this.isPaidEntirelyWithBonus()) &&
          this.order.refundedCartItems === undefined){
        return true;
      } else {
        return false;
      }
    },
    showInfoRefundNotPossible(){
      if( (this.useFunctionality() === 'TAKEAWAY' || this.useFunctionality() === 'KIOSK_TAKEAWAY' || this.useFunctionality() === 'ALL') &&
          this.order.channel === 'WEBSHOP' &&
          this.order.settled === true &&
          (OrderUtil.getPaymentStatus(this.order.payments) === 'PAID' || this.isPaidEntirelyWithBonus()) &&
          this.order.refundedCartItems === undefined){
        return true;
      } else {
        return false;
      }
    },
    /**
     * Returns true if the order was paid 100% with bonus points (no payment provider payment).
     * Used to allow refund button for orders where payments array has no PAID entry.
     */
    isPaidEntirelyWithBonus(){
      let usedPoints = this.order.usedPoints || 0;
      let manualDiscount = this.order.manualDiscount || 0;
      return usedPoints > 0 && usedPoints >= (this.order.totalAmount - manualDiscount);
    },
    /**
     * The refunded lines, on every order that has them. It was shown only to TAKEAWAY, KIOSK_TAKEAWAY and ALL merchants,
     * so a POS merchant never saw what was refunded of a till order (a delregning among them).
     */
    showRefundCartItems(){
      return Array.isArray(this.order.refundedCartItems) && this.order.refundedCartItems.length > 0;
    },
    useFunctionality(){
      return MerchantUtil.useFunctionality();
    },
    /**
     * The page that refunds this till or kiosk order's VIVA card payment from Backoffice, or null when it
     * cannot be refunded here: SUPER_ADMIN only for now, VIVA card only (cash has to be handed back at the
     * till), not yet in a Z-report, a single payment refunded once, a pay-my-part while any payment has
     * something left. The server decides all of this again (VivaTerminalRefundPolicy) — this only decides
     * whether to offer it.
     */
    vivaTerminalRefundRoute(){
      if(!this.isSuperAdmin || !this.isVivaCardOnlyTerminalOrder() || this.order.settled === true){
        return null;
      }
      if(this.order.payments.length === 1){
        return this.order.refundedCartItems === undefined ? 'vivaTerminalRefund' : null;
      }
      return this.hasVivaPaymentLeftToRefund() ? 'vivaTerminalPayMyPartRefund' : null;
    },
    showInfoTerminalRefundNotPossible(){
      return this.isSuperAdmin && this.isVivaCardOnlyTerminalOrder() && this.order.settled === true
          && this.order.refundedCartItems === undefined;
    },
    isVivaCardOnlyTerminalOrder(){
      if(this.order.channel !== 'POS' && this.order.channel !== 'KIOSK'){
        return false;
      }
      const payments = this.order.payments || [];
      if(payments.length === 0 || !payments.every(payment => this.isVivaCardPayment(payment))){
        return false;
      }
      return payments.length === 1 || this.order.paymentMethod === 'PAY_MY_PART';
    },
    // TastyPOS writes PAID_WITH_CARD on a VIVA payment, TastyKIOSK writes PAID.
    isVivaCardPayment(payment){
      return payment.onlinePaymentProvider === 'VIVA' && !!payment.transactionId
          && (payment.paymentStatus === 'PAID_WITH_CARD' || payment.paymentStatus === 'PAID');
    },
    hasVivaPaymentLeftToRefund(){
      const refunded = this.order.refundedPayments || [];
      return this.order.payments.some(payment => {
        const refundedOre = refunded
            .filter(r => r.originalTransactionId === payment.transactionId)
            .reduce((sum, r) => sum + Math.round((r.amount || 0) * 100), 0);
        return Math.round((payment.amount || 0) * 100) - refundedOre > 0;
      });
    },
    goToVivaTerminalRefundPage(){
      const route = this.vivaTerminalRefundRoute();
      this.$emit('close');
      this.$router.push({ name: route, query: { orderDocumentId: this.orderDocumentId } });
    },
    goToOrderRefundPage(){
      this.$emit('close');

      this.$router.push({ path: 'orderRefund', query: {
          orderDocumentId: this.orderDocumentId
        }
      });
    },
    sendReceipt(){
      if(!this.receiptEmail) return;
      let email = this.receiptEmail;
      this.sendingReceipt = true;
      OrderService.sendReceipt(this.orderDocumentId, email).then(
          (response) => {
            let result = response.data.result;
            if(result === 'SUCCEEDED'){
              this.showReceiptEmailInput = false;
              this.receiptEmail = '';
              this.$refs.receiptDialog.show({
                title: 'Kvittering sendt',
                message: 'Kvitteringen har blitt sendt til epost: ' + email,
                okButton: 'OK'
              });
            } else {
              this.$refs.receiptDialog.show({
                title: 'Feil',
                message: 'Kunne ikke sende kvittering.',
                okButton: 'OK'
              });
            }
            this.sendingReceipt = false;
          },
          (error) => {
            console.log(error);
            this.$refs.receiptDialog.show({
              title: 'Feil',
              message: 'Kunne ikke sende kvittering.',
              okButton: 'OK'
            });
            this.sendingReceipt = false;
          }
      );
    },
    getPaymentsSum(){
      let sum = 0;
      if(this.order.payments){
        for(let i = 0; i < this.order.payments.length; i++){
          sum += this.order.payments[i].amount || 0;
        }
      }
      return MathUtil.roundTwoDecimals(sum);
    },
    openRepairEditor(){
      this.showRepairEditor = true;
      this.repairOrderType = this.order.orderType;
      this.repairLoadingItems = true;
      this.itemsById = {};
      ProductService.getItems().then(
          (response) => {
            let items = response.data;
            if(typeof items === 'string'){
              try { items = JSON.parse(items); } catch(e) { items = []; }
            }
            if(!Array.isArray(items) && items && Array.isArray(items.items)){
              items = items.items;
            }
            if(Array.isArray(items)){
              for(let i = 0; i < items.length; i++){
                this.itemsById[items[i].id] = items[i];
              }
            }
            this.repairLoadingItems = false;
            this.recompute();
          },
          (error) => {
            console.log(error);
            this.repairLoadingItems = false;
            this.recompute(); //fallback: keep current prices
          }
      );
    },
    closeRepairEditor(){
      this.showRepairEditor = false;
    },
    setRepairOrderType(orderType){
      this.repairOrderType = orderType;
      this.recompute();
    },
    recompute(){
      let orderType = this.repairOrderType;
      let cartItems = this.order.cartItems || [];
      let lines = [];
      let totalGross = 0;
      let totalVat = 0;
      for(let i = 0; i < cartItems.length; i++){
        let cartItem = cartItems[i];
        let item = this.itemsById[cartItem.itemId];
        let rate = VatUtil.getVatRate(this.vat, orderType, cartItem.itemType);
        let missing = false;
        let baseNet;
        //Configured menu net price for the chosen type. Open-price items have no menu price (their
        //catalog price is 0), so treat that as "no catalog price" even if the openPrice flag isn't
        //in the items payload.
        //Menu-builder items with variants carry their price on the chosen subselection, not on the
        //parent item, so look up the selected variant's catalog price for the chosen order type first.
        let subselection = cartItem.itemSubselection;
        let catalogNet;
        if(item && subselection && subselection.name && Array.isArray(item.subselections)){
          let catalogSubselection = item.subselections.find(s => s.name === subselection.name);
          if(catalogSubselection){
            catalogNet = (orderType === 'DINE_IN') ? catalogSubselection.priceDineIn : catalogSubselection.price;
          }
        }
        if(catalogNet === undefined){
          catalogNet = item ? ((orderType === 'DINE_IN') ? item.priceDineIn : item.price) : undefined;
        }
        let hasCatalogPrice = catalogNet !== undefined && catalogNet !== null && catalogNet !== '' && parseFloat(catalogNet) > 0;
        if((item && item.openPrice) || !hasCatalogPrice){
          //Open-price line: the cashier entered a VAT-inclusive amount, kept undiscounted in
          //originalPrice. Preserve that gross and re-derive the net for the chosen type's VAT rate,
          //so the line stays equal to what the customer was actually charged instead of collapsing
          //to a 0 catalog price.
          let enteredGross = parseFloat(cartItem.originalPrice);
          if(!isNaN(enteredGross) && enteredGross > 0){
            baseNet = enteredGross / (1 + rate / 100);
          } else {
            baseNet = cartItem.price; //fallback: no entered gross recorded, keep stored net
            missing = !item; //only flag "not found" when the catalog item is truly missing
          }
        } else {
          baseNet = catalogNet; //configured net price for chosen type
        }
        baseNet = parseFloat(baseNet) || 0;
        if(OrderUtil.isShare(cartItem)){
          //A share keeps its own amount: the catalog price is the whole item's.
          baseNet = parseFloat(cartItem.price) || 0;
          missing = false;
        }
        //Add-ons: keep their net price, re-apply the chosen type's VAT rate. Item add-ons live under
        //addOnCartItems; variant add-ons live under the chosen subselection's addOnCartItems.
        let addOnsNet = 0;
        let addOns = cartItem.addOnCartItems || [];
        if(subselection && Array.isArray(subselection.addOnCartItems)){
          addOns = addOns.concat(subselection.addOnCartItems);
        }
        for(let a = 0; a < addOns.length; a++){
          addOnsNet += (parseFloat(addOns[a].price) || 0) * (addOns[a].count || 1);
        }
        let discountPercent = cartItem.discountPercent || 0;
        let unitNet = baseNet + addOnsNet;
        let unitGross = OrderUtil.getPriceWithVat(rate, unitNet);
        let lineGross = MathUtil.roundTwoDecimals(unitGross * cartItem.count * (100 - discountPercent) / 100);
        let lineNet = MathUtil.roundTwoDecimals(unitNet * cartItem.count * (100 - discountPercent) / 100);
        let lineVat = MathUtil.roundTwoDecimals(lineGross - lineNet);
        totalGross += lineGross;
        totalVat += lineVat;
        lines.push({
          id: cartItem.id,
          name: (cartItem.itemName || 'Vare') + OrderUtil.shareSuffix(cartItem),
          count: cartItem.count,
          baseNet: MathUtil.roundTwoDecimals(baseNet),
          unitGross: MathUtil.roundTwoDecimals(unitGross),
          lineGross: lineGross,
          lineVat: lineVat,
          rate: rate,
          discountPercent: discountPercent,
          missing: missing
        });
      }
      this.repairedLines = lines;
      this.repairedTotalAmount = MathUtil.roundTwoDecimals(totalGross);
      this.repairedTotalVat = MathUtil.roundTwoDecimals(totalVat);
      let manualDiscount = this.order.manualDiscount ? this.order.manualDiscount : 0;
      let tips = this.order.tipsAmount ? this.order.tipsAmount : 0;
      this.repairedToPayAmount = MathUtil.roundTwoDecimals(this.repairedTotalAmount + tips - manualDiscount);
    },
    applyRepair(){
      let cartItems = JSON.parse(JSON.stringify(this.order.cartItems || []));
      let linesById = {};
      for(let i = 0; i < this.repairedLines.length; i++){
        linesById[this.repairedLines[i].id] = this.repairedLines[i];
      }
      let vats = [];
      for(let i = 0; i < cartItems.length; i++){
        let cartItem = cartItems[i];
        let line = linesById[cartItem.id];
        if(line){
          cartItem.price = line.baseNet; //updated base net price for chosen order type
          vats.push({
            cartItemId: cartItem.id,
            amountWithVat: line.lineGross,
            vatAmount: line.lineVat,
            vatRate: line.rate,
            discount: 0,
            discountPercent: line.discountPercent
          });
        }
      }
      let repairedBody = {
        orderType: this.repairOrderType,
        cartItems: cartItems,
        vats: vats,
        totalAmount: this.repairedTotalAmount,
        totalVat: this.repairedTotalVat,
        toPayAmount: this.repairedToPayAmount
      };
      this.repairingOrder = true;
      OrderService.repairOrderData(this.orderDocumentId, repairedBody).then(
          (response) => {
            let result = response.data.result;
            if(result === 'SUCCEEDED'){
              this.showRepairEditor = false;
              this.$refs.receiptDialog.show({
                title: 'Order repaired',
                message: 'The order data has been repaired.',
                okButton: 'OK'
              }).then(() => this.$router.go());
            } else {
              this.$refs.receiptDialog.show({
                title: 'Error',
                message: 'Could not repair the order data.',
                okButton: 'OK'
              });
            }
            this.repairingOrder = false;
          },
          (error) => {
            console.log(error);
            this.$refs.receiptDialog.show({
              title: 'Error',
              message: 'Could not repair the order data.',
              okButton: 'OK'
            });
            this.repairingOrder = false;
          }
      );
    },
    markAsCompleted(){
      this.loading = true;

      OrderService.updateOrderStatus(this.orderDocumentId, 'COMPLETED').then(
          (response) => {
            let result = response.data.result;
            if(result == 'SUCCEEDED'){
              this.$router.go(); //Reload current page
            } else {
              console.log("Order markAsCompleted failed");
            }
            this.loading = false;
          },
          (error) => {
            console.log(error);
          }
      );
    },
    displayTwoDecimals(num){
      if(num > 0 || num < 0){
        return MathUtil.displayTwoDecimals(num);
      }else {
        return num;
      }
    }
  }
}
</script>

<style scoped>
  .detail-container {
    padding: 0;
    max-width: 560px;
  }

  /* Header */
  .detail-header {
    padding: 20px 20px 16px;
    border-left: 4px solid var(--bo-muted-2);
    background: linear-gradient(135deg, var(--bo-surface-2) 0%, var(--bo-surface) 100%);
    margin-bottom: 4px;
  }

  .detail-header-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  /*
   * Order status colors:
   *   ORDERED   — yellow: needs attention
   *   CONFIRMED — green: being prepared
   *   COMPLETED — no color (default var(--bo-muted-2)): done, out of focus
   *   CANCELLED — red: problem
   */
  .header-ordered { border-left-color: #f59e0b; }
  .header-confirmed { border-left-color: #22c55e; }
  .header-completed { border-left-color: var(--bo-muted-2); }
  .header-cancelled { border-left-color: #ef4444; }

  .detail-location {
    font-size: 13px;
    color: var(--bo-ink);
    display: block;
    margin-bottom: 4px;
  }

  .detail-order-type {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--bo-ink);
    margin-bottom: 2px;
  }

  .detail-order-id {
    font-size: 22px;
    font-weight: 700;
    color: var(--bo-ink);
  }

  .detail-status-badge {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 12px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .detail-status-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .btn-send-receipt {
    padding: 4px 12px;
    font-size: 12px;
    min-width: auto;
    white-space: nowrap;
  }

  .btn-repair-order {
    padding: 4px 12px;
    font-size: 12px;
    min-width: auto;
    white-space: nowrap;
  }

  .receipt-email-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .receipt-email-input {
    padding: 4px 10px;
    font-size: 13px;
    border: 1px solid var(--bo-line);
    border-radius: 6px;
    outline: none;
    width: 200px;
  }

  .receipt-email-input:focus {
    border-color: #ECB109;
    box-shadow: 0 0 0 2px rgba(236, 177, 9, 0.2);
  }

  .btn-cancel-receipt {
    padding: 4px 8px;
    font-size: 13px;
    color: var(--bo-muted);
    background: none;
    border: none;
    cursor: pointer;
  }

  .btn-cancel-receipt:hover {
    color: var(--bo-ink);
  }

  .status-ordered { background: var(--bo-warn-bg); color: var(--bo-warn-fg); }
  .status-confirmed { background: var(--bo-ok-bg); color: var(--bo-ok-fg); }
  .status-completed { background: var(--bo-surface-2); color: var(--bo-ink); }
  .status-cancelled { background: var(--bo-bad-bg); color: var(--bo-bad-fg); }
  .status-draft { background: var(--bo-warn-bg); color: var(--bo-warn-fg); } /* «Åpen»: still open on the till */

  .btn-mark-completed {
    margin-top: 12px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #fff;
    background: #22c55e;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-mark-completed:hover {
    background: #16a34a;
  }

  /* Sections */
  .detail-section {
    padding: 16px 20px;
    border-bottom: 1px solid var(--bo-surface-2);
  }

  .detail-section:last-child {
    border-bottom: none;
  }

  .detail-section-title {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
    color: var(--bo-ink);
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 12px;
  }

  /* The icon before a heading: larger than the heading's small capitals, so it is seen at a glance, on the till too
     (founder, 2026-10-28). On one width, so the headings start in line. */
  .detail-section-title > .svg-inline--fa {
    font-size: 1.4em;
    width: 1.25em;
  }

  /* Info grid */
  .detail-info-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .detail-info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .detail-label {
    font-size: 14px;
    color: var(--bo-ink);
  }

  .detail-value {
    font-size: 14px;
    font-weight: 500;
    color: var(--bo-ink);
  }

  .detail-name {
    font-size: 17px;
    font-weight: 600;
  }

  .detail-address {
    text-align: right;
  }

  .detail-address a {
    color: var(--bo-ink);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .detail-amount {
    font-size: 16px;
    font-weight: 700;
  }

  .detail-present-counter {
    font-size: 20px;
    font-weight: 700;
  }

  .detail-discount {
    color: #ef4444;
  }

  /* Payment badge */
  .detail-payment-badge {
    font-size: 12px;
    font-weight: 500;
    padding: 3px 10px;
    border-radius: 12px;
  }

  .payment-paid-badge {
    background: var(--bo-ok-bg);
    color: var(--bo-ok-fg);
  }

  .payment-unpaid-badge {
    background: var(--bo-bad-bg);
    color: var(--bo-bad-fg);
  }

  /* Paid AND completed: the neutral surface of the COMPLETED status badge, as in the orders list */
  .payment-completed-badge {
    background: var(--bo-surface-2);
    color: var(--bo-ink);
  }

  /* Total row */
  .detail-total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 2px solid var(--bo-line);
    font-size: 15px;
    font-weight: 600;
    color: var(--bo-ink);
  }

  .detail-total-amount {
    font-size: 18px;
    font-weight: 700;
  }

  /* Refund section */
  .detail-refund-section {
    background: var(--bo-bad-bg);
    border-left: 3px solid #ef4444;
    border-bottom: 1px solid var(--bo-bad-line);
  }

  .detail-refund-title {
    color: var(--bo-bad-fg);
  }

  .detail-refund-list {
    display: flex;
    flex-direction: column;
  }

  .detail-refund-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 8px 0;
    border-bottom: 1px solid var(--bo-bad-line);
  }

  .detail-refund-item:last-child {
    border-bottom: none;
  }

  .detail-refund-item-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--bo-bad-fg);
  }

  .detail-refund-item-sub {
    font-size: 12px;
    color: var(--bo-alert-ink);
    margin-top: 2px;
  }

  .detail-refund-item-price {
    font-size: 14px;
    font-weight: 600;
    color: var(--bo-bad-fg);
    white-space: nowrap;
  }

  .detail-refund-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 2px solid var(--bo-bad-line);
    font-size: 14px;
    font-weight: 600;
    color: var(--bo-bad-fg);
  }

  .detail-refund-total-amount {
    font-size: 16px;
    font-weight: 700;
  }

  /* Payments and delregninger: one row each */
  .detail-line-list {
    display: flex;
    flex-direction: column;
  }

  .detail-line-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid var(--bo-surface-2);
  }

  .detail-line-item:last-child {
    border-bottom: none;
  }

  .detail-line-item-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--bo-ink);
  }

  .detail-line-item-sub {
    font-size: 12px;
    color: var(--bo-muted);
    margin-top: 2px;
    word-break: break-all;
  }

  .detail-line-item-amount {
    font-size: 14px;
    font-weight: 600;
    color: var(--bo-ink);
    white-space: nowrap;
    text-align: right;
  }

  .detail-line-item-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .detail-split-text {
    font-size: 14px;
    color: var(--bo-ink);
    margin-bottom: 8px;
  }

  .detail-split-open {
    min-width: 80px;
  }

  /* Description */
  .detail-description {
    white-space: pre-wrap;
    background: var(--bo-surface-2);
    border-radius: 6px;
    padding: 12px;
    font-size: 14px;
    color: var(--bo-ink);
    line-height: 1.5;
  }

  /* Refund button */
  .btn-refund {
    width: 100%;
    padding: 12px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    background: #ef4444;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-refund:hover {
    background: #dc2626;
  }

  /* Repair editor */
  .repair-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1080;
    padding: 16px;
  }

  .repair-modal {
    background: var(--bo-surface);
    border-radius: 10px;
    width: 100%;
    max-width: 520px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
  }

  .repair-modal-header {
    display: flex;
    align-items: center;
    padding: 16px 20px;
    font-size: 16px;
    font-weight: 700;
    color: var(--bo-ink);
    border-bottom: 1px solid var(--bo-surface-2);
  }

  .repair-close {
    margin-left: auto;
    background: none;
    border: none;
    font-size: 18px;
    color: var(--bo-muted);
    cursor: pointer;
  }

  .repair-close:hover { color: var(--bo-ink); }

  .repair-modal-body {
    padding: 16px 20px;
    overflow-y: auto;
  }

  .repair-help {
    font-size: 13px;
    color: var(--bo-muted);
    margin-bottom: 14px;
    line-height: 1.4;
  }

  .repair-type-toggle {
    display: flex;
    gap: 10px;
    margin-bottom: 16px;
  }

  .repair-type-btn {
    flex: 1;
    opacity: 0.6;
  }

  .repair-type-active {
    opacity: 1;
    box-shadow: 0 0 0 2px #ECB109;
  }

  .repair-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    margin-bottom: 14px;
  }

  .repair-table th {
    text-align: left;
    color: var(--bo-muted);
    font-weight: 600;
    border-bottom: 1px solid var(--bo-line);
    padding: 6px 4px;
  }

  .repair-table td {
    padding: 6px 4px;
    border-bottom: 1px solid var(--bo-surface-2);
    color: var(--bo-ink);
  }

  .repair-num { text-align: right; }

  .repair-line-missing { color: var(--bo-brand-ink); }

  .repair-missing-tag {
    font-size: 11px;
    color: var(--bo-brand-ink);
  }

  .repair-totals {
    border-top: 2px solid var(--bo-line);
    padding-top: 10px;
  }

  .repair-row {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    padding: 3px 0;
    color: var(--bo-ink);
  }

  .repair-row-total {
    font-weight: 700;
    color: var(--bo-ink);
  }

  .repair-compare {
    margin-top: 8px;
    padding: 8px 10px;
    border-radius: 6px;
    font-weight: 600;
  }

  .repair-ok { background: var(--bo-ok-bg); color: var(--bo-ok-fg); }
  .repair-warn { background: var(--bo-bad-bg); color: var(--bo-bad-fg); }

  .repair-note {
    font-size: 13px;
    color: var(--bo-muted);
    padding: 12px 0;
  }

  .repair-modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px;
    border-top: 1px solid var(--bo-surface-2);
  }

  .repair-apply {
    background: #22c55e;
    color: #fff;
  }

  .repair-apply:hover { background: #16a34a; }

  .repair-apply:disabled {
    background: var(--bo-line);
    color: var(--bo-muted);
    cursor: not-allowed;
  }

  /* Refund notice */
  .detail-refund-notice {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    padding: 12px;
    background: var(--bo-brand-tint);
    border: 1px solid var(--bo-warn-line);
    border-radius: 8px;
    font-size: 13px;
    color: var(--bo-warn-fg);
    line-height: 1.4;
  }

  /* Who may use the button below it, and that the guest's card is not needed. */
  .detail-refund-hint {
    margin-top: 6px;
    font-size: 12px;
    color: var(--bo-muted);
  }
</style>
